import { useCallback, useMemo, useState } from 'react'
import type { ShipDto } from '@/api/hooks'

/** Clé de la pastille « Équipés » : au moins un module, quel qu'il soit. */
export const ANY_MODULE = '__any__'

export type ShipSortKey = 'arrivee' | 'power' | 'cargo' | 'name'

/**
 * `arrivee` EN PREMIER, partout : c'est l'ordre du payload (`createdAt ASC`,
 * ShipRepository::findByOwnerInSeason), donc l'ordre d'arrivée à quai, et c'est
 * celui que les onze listes rendent depuis toujours. Un autre défaut
 * réordonnerait les onze écrans au déploiement et rendrait cet ordre
 * inatteignable.
 *
 * Les clés sont contextuelles pour ne jamais proposer un tri dénué de sens :
 * pas de tri par cale devant un repaire à piller.
 */
export const COMBAT_SORTS: ShipSortKey[] = ['arrivee', 'power', 'name']
export const CARGO_SORTS: ShipSortKey[] = ['arrivee', 'cargo', 'name']

export const SORT_LABEL: Record<ShipSortKey, string> = {
  arrivee: 'Arrivée',
  power: 'Puissance',
  cargo: 'Cale',
  name: 'Nom',
}

/**
 * Règle pure de correspondance. Deux contraintes indépendantes, chacune avec la
 * règle maison « set vide = tout visible » (cf. `isTypeVisible` du filtre de
 * carte) :
 *   - coques : appartenance simple ;
 *   - modules : `ANY_MODULE` exige un équipement quelconque, et les modules
 *     nommés sont en OU entre eux. Un ET rendrait des listes vides en
 *     permanence, un brick n'ayant que deux slots.
 *
 * `ship.modules` est lu en optionnel : le champ est absent de plusieurs fixtures
 * de test (Chantier.test.tsx, KrakenPanel.test.tsx), et un accès direct y lèverait
 * un TypeError au lieu de ne pas matcher.
 */
export function shipMatchesFilter(
  ship: ShipDto,
  hulls: ReadonlySet<string>,
  modules: ReadonlySet<string>,
): boolean {
  if (hulls.size > 0 && !hulls.has(ship.shipType)) return false
  if (modules.size === 0) return true

  const fitted = ship.modules ?? []
  if (modules.has(ANY_MODULE) && fitted.length === 0) return false

  const named = [...modules].filter((m) => m !== ANY_MODULE)
  if (named.length === 0) return true
  return named.some((wanted) => fitted.some((f) => f.type === wanted))
}

/**
 * Score de létalité d'une coque. Miroir EXACT de
 * `ShipCombatStats::ownPower()` (api/src/Domain/Combat/ShipCombatStats.php:125) :
 * bordée + abordage + manœuvre + hp/3 + armure×2.
 *
 * `combatStats` est déjà le profil EFFECTIF (dés des modules + bonus de coque
 * Forge), donc ce score reflète la coque réellement équipée, ce que
 * `ownPower()` est justement documenté pour permettre.
 */
export function shipPower(s: ShipDto): number {
  const cs = s.combatStats
  return cs.bordee + cs.abordage + cs.manoeuvre + Math.floor(cs.hp / 3) + cs.armure * 2
}

/** Trie une copie. `arrivee` rend le tableau tel quel : le payload EST cet ordre. */
export function sortShips(ships: ShipDto[], key: ShipSortKey): ShipDto[] {
  if (key === 'arrivee') return ships
  const out = [...ships]
  switch (key) {
    case 'name':
      out.sort((a, b) => a.name.localeCompare(b.name, 'fr'))
      break
    case 'cargo':
      out.sort((a, b) => b.cargoCapacity - a.cargoCapacity)
      break
    case 'power':
      out.sort((a, b) => shipPower(b) - shipPower(a))
      break
  }
  return out
}

export interface ShipFilter {
  hulls: ReadonlySet<string>
  modules: ReadonlySet<string>
  sort: ShipSortKey
  sorts: ShipSortKey[]
  toggleHull: (t: string) => void
  toggleModule: (m: string) => void
  resetHulls: () => void
  cycleSort: () => void
}

/**
 * État de filtre d'un picker. Local à l'instance, donc PERDU à la fermeture du
 * panneau, comme la sélection elle-même : un filtre qui survivrait à la
 * fermeture réapparaîtrait sans que rien à l'écran ne l'explique.
 */
export function useShipFilter(sorts: ShipSortKey[]): ShipFilter {
  const [hulls, setHulls] = useState<Set<string>>(() => new Set())
  const [modules, setModules] = useState<Set<string>>(() => new Set())
  const [sortIndex, setSortIndex] = useState(0)

  const toggleHull = useCallback((key: string) => {
    setHulls((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  const toggleModule = useCallback((key: string) => {
    setModules((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  const resetHulls = useCallback(() => setHulls(new Set()), [])
  const cycleSort = useCallback(
    () => setSortIndex((i) => (i + 1) % sorts.length),
    [sorts.length],
  )

  return useMemo(
    () => ({
      hulls, modules,
      sort: sorts[sortIndex % sorts.length] ?? 'arrivee',
      sorts,
      toggleHull, toggleModule, resetHulls, cycleSort,
    }),
    [hulls, modules, sortIndex, sorts, toggleHull, toggleModule, resetHulls, cycleSort],
  )
}
